/* THE SURVEY'S GEOMETRY KIT. Pure: typed arrays out, no three.js, no clock, no chance. Every coordinate is quantised to
   1/1024 before it is stored, so the arrays (and the golden hash over them) are the same on every engine. Lattice points are
   whole numbers and are never quantised: they are multiples of the grid unit by construction. */
import type { Pattern } from '../licensee/model/theatre.ts'

export const q = (v: number): number => Math.round(v * 1024) / 1024

/** One layer of the world: positions (xyz triples), and for lines the centre each vertex breathes about and its phase. `offsets[k]` is the number of vertices
    before stratum k, so stratum k is [offsets[k], offsets[k+1]). A layer with one stratum is drawn whole. */
export type Strata = { pos: Float32Array; ctr: Float32Array; phase: Float32Array; offsets: Uint32Array }

export class Acc {
  pos: number[] = []
  ctr: number[] = []
  phase: number[] = []
  offsets: number[] = [0]
  readonly lines: boolean
  /** Whether segments carry the centre they breathe about; a still world leaves it out and saves two thirds of the vertex memory. */
  readonly breath: boolean
  constructor(lines = false, breath = false) {
    this.lines = lines
    this.breath = breath
  }
  point(x: number, y: number, z: number): void {
    this.pos.push(q(x), q(y), q(z))
  }
  segment(a: readonly number[], b: readonly number[], c: readonly number[] = a, phase = 0): void {
    this.pos.push(q(a[0]), q(a[1]), q(a[2]), q(b[0]), q(b[1]), q(b[2]))
    if (!this.breath) return
    this.ctr.push(c[0], c[1], c[2], c[0], c[1], c[2])
    this.phase.push(phase, phase)
  }
  /** Ends the current stratum. */
  close(): void {
    this.offsets.push(this.pos.length / 3)
  }
  done(): Strata {
    return { pos: new Float32Array(this.pos), ctr: new Float32Array(this.ctr), phase: new Float32Array(this.phase), offsets: new Uint32Array(this.offsets) }
  }
}

/** The unit circle for n steps, quantised. */
const TABLES = new Map<number, { c: number[]; s: number[] }>()
export function circle(n: number): { c: number[]; s: number[] } {
  let t = TABLES.get(n)
  if (!t) {
    t = { c: Array.from({ length: n }, (_, i) => q(Math.cos((2 * Math.PI * i) / n))), s: Array.from({ length: n }, (_, i) => q(Math.sin((2 * Math.PI * i) / n))) }
    TABLES.set(n, t)
  }
  return t
}

/** How many of a loop's n segments are missing at a break. */
export const gapOf = (n: number): number => Math.max(1, Math.floor(n / 4))

/** A closed loop of n vertices, drawn by its state: whole when ok, two segments in four when thinning (a dash), with a gap when failing.
    A gap's two ends go to `breaks` (the only place an alarm colour is drawn). `at(i)` is vertex i. */
export function loop(lines: Acc, breaks: { point(x: number, y: number, z: number): void } | null, n: number, at: (i: number) => [number, number, number], ctr: readonly number[], phase: number, state: Pattern, breakAt: number): void {
  const gap = gapOf(n)
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    if (state === 'degrading' && i % 4 >= 2) continue
    if (state === 'failing' && (i - breakAt + n) % n < gap) continue
    lines.segment(at(i), at(j), ctr, phase)
  }
  if (state === 'failing' && breaks) {
    const a = at(breakAt % n)
    const b = at((breakAt + gap) % n)
    breaks.point(a[0], a[1], a[2])
    breaks.point(b[0], b[1], b[2])
  }
}

/** A horizontal circle of radius r about (cx, cy, cz). */
export function flatRing(lines: Acc, breaks: { point(x: number, y: number, z: number): void } | null, cx: number, cy: number, cz: number, r: number, n: number, state: Pattern, breakAt: number, phase: number): void {
  const { c, s } = circle(n)
  loop(lines, breaks, n, (i) => [cx + r * c[i], cy, cz + r * s[i]], [cx, cy, cz], phase, state, breakAt)
}

/** Vertices of a horizontal circle, for the dots a scan resolves. */
export function ringDots(dots: Acc, cx: number, cy: number, cz: number, r: number, n: number): void {
  const { c, s } = circle(n)
  for (let i = 0; i < n; i++) dots.point(cx + r * c[i], cy, cz + r * s[i])
}

export const RANK: Record<Pattern, number> = { ok: 0, degrading: 1, failing: 2 }
export const worstOf = (ps: readonly Pattern[]): Pattern => ps.reduce<Pattern>((a, p) => (RANK[p] > RANK[a] ? p : a), 'ok')

/** A seeded generator (mulberry32): the one source of chance in the survey, so a sampled solid is the same dots on every engine. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** The alarm dots: each belongs to a stratum and either stays (a harm event's mark) or shows only while the plane is at that stratum (a membrane's break). */
export class AlarmAcc {
  pos: number[] = []
  at: number[] = []
  keep: number[] = []
  /** The stratum and persistence of the dots pushed next. */
  k = 0
  stays = false
  point(x: number, y: number, z: number): void {
    this.pos.push(q(x), q(y), q(z))
    this.at.push(this.k)
    this.keep.push(this.stays ? 1 : 0)
  }
  done(): Alarm {
    return { pos: new Float32Array(this.pos), at: new Float32Array(this.at), keep: new Float32Array(this.keep) }
  }
}
export type Alarm = { pos: Float32Array; at: Float32Array; keep: Float32Array }

/** Line segments as dots: `per` dots along each segment, so a membrane is a continuous run of fat dots. Stratum offsets stay whole strata. */
export function dotted(s: Strata, per = 2): Strata {
  const segs = s.pos.length / 6
  const pos = new Float32Array(segs * per * 3)
  for (let i = 0; i < segs; i++) {
    for (let j = 0; j < per; j++) {
      const t = (j + 0.5) / per
      for (let c = 0; c < 3; c++) pos[(i * per + j) * 3 + c] = s.pos[i * 6 + c] * (1 - t) + s.pos[i * 6 + 3 + c] * t
    }
  }
  return { pos, ctr: new Float32Array(0), phase: new Float32Array(0), offsets: Uint32Array.from(s.offsets, (v) => (v / 2) * per) }
}
